import React, { useMemo } from "react";
import { ArrowLeft, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { Badge } from "../../../ui/badge";
import { Button } from "../../../ui/button";
import { getStakeholderLaneAppearance, LANE_STATUS_META } from "../../config/stakeholderLaneConfig";

function GuideCard({ item, onOpen }) {
  const appearance = getStakeholderLaneAppearance(item.lane);
  const disablePersonRouteActions = !!item.disable_person_route_actions;

  return (
    <button
      className={`w-full rounded-2xl border border-slate-200/80 bg-white/90 p-4 text-left shadow-sm transition hover:shadow-md dark:border-white/10 dark:bg-white/[0.04] ${appearance.cardHoverClass} ${disablePersonRouteActions ? "cursor-not-allowed opacity-70" : ""}`}
      disabled={disablePersonRouteActions}
      onClick={() => onOpen(item.id)}
      type="button"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-slate-950 dark:text-white">{item.title}</p>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">{item.reason}</p>
        </div>
        {item.is_kevin_flagged ? <Badge>{item.urgency_label || "Direct"}</Badge> : null}
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        {item.guide_status_label ? <Badge className={`border ${appearance.chipClass}`} variant="outline">{item.guide_status_label}</Badge> : null}
        {item.invite_stage_label ? <Badge className={`border ${appearance.chipClass}`} variant="outline">{item.invite_stage_label}</Badge> : null}
        {item.scheduled_date_label ? <Badge className={`border ${appearance.chipClass}`} variant="outline">Scheduled {item.scheduled_date_label}</Badge> : null}
      </div>

      {item.next_move ? (
        <p className={`mt-3 text-xs font-medium uppercase tracking-[0.16em] ${appearance.accentTextClass}`}>
          Next: {item.next_move}
        </p>
      ) : null}
      {item.last_meaningful_movement ? (
        <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">{item.last_meaningful_movement}</p>
      ) : null}
      {item.latest_contact_summary ? (
        <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">{item.latest_contact_summary}</p>
      ) : null}
      {item.next_follow_up_summary ? (
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{item.next_follow_up_summary}</p>
      ) : null}
      {item.contact_label ? (
        <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">{item.contact_label}</p>
      ) : null}
    </button>
  );
}

function Section({ children, className = "", description, title }) {
  return (
    <section className={`rounded-[26px] border border-slate-200/80 bg-slate-50/85 p-5 dark:border-white/10 dark:bg-white/[0.03] ${className}`}>
      <div className="border-b border-slate-200/80 pb-4 dark:border-white/10">
        <h2 className="text-lg font-semibold text-slate-950 dark:text-white">{title}</h2>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">{description}</p>
      </div>
      <div className="mt-4">{children}</div>
    </section>
  );
}

export default function GuidePartnersLanePage({ lane, laneConfig, onOpen }) {
  const navigate = useNavigate();
  const statusMeta = LANE_STATUS_META[lane.status];
  const appearance = getStakeholderLaneAppearance(lane.id);

  const needsNowItems = useMemo(() => lane.items["needs-now"] || [], [lane.items]);
  const movingItems = useMemo(() => lane.items.moving || [], [lane.items]);
  const watchItems = useMemo(() => lane.items.watch || [], [lane.items]);
  const allGuides = useMemo(() => [...needsNowItems, ...movingItems, ...watchItems], [needsNowItems, movingItems, watchItems]);

  const activationSignals = useMemo(() => {
    const scheduledCount = allGuides.filter((item) => item.scheduled_date_label).length;
    const activeCount = movingItems.length;
    const needsCoachCount = needsNowItems.length;
    const invitedCount = allGuides.filter((item) => item.invite_stage_label === "Invited").length;

    return [
      {
        label: "Active and serving",
        value: activeCount,
        note: "Guide partners currently showing real movement, not just availability.",
      },
      {
        label: "Prep or coaching needed",
        value: needsCoachCount,
        note: "People who likely need your help before they serve well.",
      },
      {
        label: "Scheduled touchpoints",
        value: scheduledCount,
        note: "Upcoming story or time-bound opportunities already visible.",
      },
      {
        label: "Invited but not activated",
        value: invitedCount,
        note: "People who have been invited but may still need a real nudge.",
      },
    ];
  }, [allGuides, movingItems, needsNowItems]);

  const riskNotes = useMemo(() => {
    const onboardingMentions = allGuides.filter((item) =>
      `${item.reason} ${item.notes}`.toLowerCase().includes("onboarding")
    ).length;
    const scheduledMentions = allGuides.filter((item) =>
      `${item.reason} ${item.notes}`.toLowerCase().includes("scheduled")
    ).length;
    const noEmail = allGuides.filter((item) => !item.contact_email).length;

    return [
      {
        label: "Onboarding/activation drift",
        value: onboardingMentions,
      },
      {
        label: "Scheduled follow-up risks",
        value: scheduledMentions,
      },
      {
        label: "Low-contact records",
        value: noEmail,
      },
    ];
  }, [allGuides]);

  return (
    <div className={`min-h-screen pb-10 ${appearance.pageClass}`}>
      <div className="mx-auto max-w-[1440px] px-4 py-6 sm:px-6 lg:px-8">
        <div className={`rounded-[30px] border bg-white/85 p-6 shadow-[0_35px_80px_-38px_rgba(15,23,42,0.45)] backdrop-blur dark:bg-[#081018]/90 ${appearance.shellClass}`}>
          <div className="flex flex-col gap-5 border-b border-slate-200/80 pb-6 dark:border-white/10 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-4xl">
              <p className={`text-xs font-semibold uppercase tracking-[0.24em] ${appearance.headingTextClass}`}>
                Lane Detail
              </p>
              <h1 className="mt-2 text-3xl font-semibold tracking-tight text-slate-950 dark:text-white">
                {lane.title}
              </h1>
              <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-slate-300">{lane.promise}</p>
              <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
                {laneConfig.charter}
              </p>
            </div>

            <div className="flex items-center gap-3">
              <Badge className={`border ${statusMeta.className}`}>{statusMeta.label}</Badge>
              <Button onClick={() => navigate("/community")} variant="outline">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Community Home
              </Button>
            </div>
          </div>

          <div className="mt-6 grid gap-4 md:grid-cols-3 xl:grid-cols-5">
            <div className="rounded-2xl border border-rose-200/70 bg-rose-50/80 p-5 dark:border-rose-400/20 dark:bg-rose-500/10">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-rose-700 dark:text-rose-100">
                Needs Now
              </p>
              <p className="mt-2 text-3xl font-semibold text-rose-950 dark:text-white">
                {lane.counts["needs-now"]}
              </p>
            </div>
            <div className="rounded-2xl border border-emerald-200/70 bg-emerald-50/80 p-5 dark:border-emerald-400/20 dark:bg-emerald-500/10">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700 dark:text-emerald-100">
                Moving
              </p>
              <p className="mt-2 text-3xl font-semibold text-emerald-950 dark:text-white">
                {lane.counts.moving}
              </p>
            </div>
            <div className="rounded-2xl border border-amber-200/70 bg-amber-50/80 p-5 dark:border-amber-400/20 dark:bg-amber-500/10">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-amber-700 dark:text-amber-100">
                Watch
              </p>
              <p className="mt-2 text-3xl font-semibold text-amber-950 dark:text-white">
                {lane.counts.watch}
              </p>
            </div>
            <div className="rounded-2xl border border-slate-200/80 bg-slate-50/90 p-5 dark:border-white/10 dark:bg-white/[0.04]">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-600 dark:text-slate-300">
                Active / serving
              </p>
              <p className="mt-2 inline-flex items-center gap-2 text-3xl font-semibold text-slate-950 dark:text-white">
                <Sparkles className={`h-6 w-6 ${appearance.accentTextClass}`} />
                {activationSignals[0].value}
              </p>
            </div>
            <div className="rounded-2xl border border-slate-200/80 bg-slate-50/90 p-5 dark:border-white/10 dark:bg-white/[0.04]">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-600 dark:text-slate-300">
                Prep/coaching needs
              </p>
              <p className="mt-2 text-3xl font-semibold text-slate-950 dark:text-white">
                {activationSignals[1].value}
              </p>
            </div>
          </div>

          <div className="mt-8 grid gap-6 xl:grid-cols-[1.4fr_0.9fr]">
            <div className="space-y-6">
              <Section
                className={appearance.panelClass}
                description="Guide Partners who need prep, re-engagement, or direct coaching now."
                title="Needs Kevin Now"
              >
                <div className="space-y-3">
                  {needsNowItems.length ? (
                    needsNowItems.map((item) => <GuideCard item={item} key={item.id} onOpen={onOpen} />)
                  ) : (
                    <p className="rounded-2xl border border-dashed border-slate-200 px-4 py-5 text-sm text-slate-500 dark:border-white/10 dark:text-slate-400">
                      No urgent Guide Partner follow-up is surfaced right now.
                    </p>
                  )}
                </div>
              </Section>

              <Section
                className={appearance.panelClass}
                description="People serving, showing warmth, or stepping into a visible role well."
                title="Serving Well"
              >
                <div className="space-y-3">
                  {movingItems.length ? (
                    movingItems.map((item) => <GuideCard item={item} key={item.id} onOpen={onOpen} />)
                  ) : (
                    <p className="rounded-2xl border border-dashed border-slate-200 px-4 py-5 text-sm text-slate-500 dark:border-white/10 dark:text-slate-400">
                      No strong service signals are being tracked yet.
                    </p>
                  )}
                </div>
              </Section>

              <Section
                className={appearance.panelClass}
                description="People who may fade after invitation, onboarding, or early activation unless seen soon."
                title="Activation Drift"
              >
                <div className="space-y-3">
                  {watchItems.length ? (
                    watchItems.map((item) => <GuideCard item={item} key={item.id} onOpen={onOpen} />)
                  ) : (
                    <p className="rounded-2xl border border-dashed border-slate-200 px-4 py-5 text-sm text-slate-500 dark:border-white/10 dark:text-slate-400">
                      No Guide Partner drift signals are surfaced right now.
                    </p>
                  )}
                </div>
              </Section>
            </div>

            <div className="space-y-6">
              <Section
                className={appearance.panelClass}
                description="Quick indicators for whether this lane is moving from invitation into useful service."
                title="Renewal Signals"
              >
                <div className="space-y-3">
                  {activationSignals.map((signal) => (
                    <div
                      className={`rounded-2xl border p-4 ${appearance.panelClass}`}
                      key={signal.label}
                    >
                      <div className="flex items-baseline justify-between gap-3">
                        <p className="text-sm font-semibold text-slate-950 dark:text-white">{signal.label}</p>
                        <p className="text-2xl font-semibold text-slate-950 dark:text-white">{signal.value}</p>
                      </div>
                      <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">{signal.note}</p>
                    </div>
                  ))}
                </div>
              </Section>

              <Section
                className={appearance.panelClass}
                description="Early warning signs that this lane is staying abstract, missing prep, or not crossing into embodied help."
                title="Intervention Triggers"
              >
                <div className="space-y-3">
                  {riskNotes.map((risk) => (
                    <div
                      className={`rounded-2xl border p-4 ${appearance.panelClass}`}
                      key={risk.label}
                    >
                      <div className="flex items-baseline justify-between gap-3">
                        <p className="text-sm font-semibold text-slate-950 dark:text-white">{risk.label}</p>
                        <p className="text-2xl font-semibold text-slate-950 dark:text-white">{risk.value}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </Section>

              <Section
                className={appearance.panelClass}
                description="Open any Guide Partner card to adjust prep, coaching, notes, and next move."
                title="Onboarding And Guidance"
              >
                <p className="text-sm leading-6 text-slate-600 dark:text-slate-300">
                  This lane should help you see who is actually serving, who still needs a clear activation path,
                  and where simple coaching could turn a willing person into a steady helper.
                </p>
              </Section>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
